/**
 * In-memory cache with TTL for namespace lookups
 */
export class NamespaceCache {
  /**
   * @param {number} ttl - Time to live in milliseconds
   */
  constructor(ttl) {
    this.ttl = ttl
    this.cache = new Map()
  }

  /**
   * Get a cached entry (with expiration check)
   * @param {string} name - Worker name
   * @returns {import('./types').CacheEntry|null} Cached entry or null if not found/expired
   */
  get(name) {
    const entry = this.cache.get(name)
    if (!entry) return null

    if (Date.now() > entry.expiresAt) {
      this.cache.delete(name)
      return null
    }

    return entry
  }

  /**
   * Cache an entry with expiration
   * @param {string} name - Worker name
   * @param {import('./types').LookupResult} data - Lookup result to cache
   */
  set(name, data) {
    const now = Date.now()
    this.cache.set(name, {
      ...data,
      timestamp: now,
      expiresAt: now + this.ttl
    })
  }

  /**
   * Batch get - returns cached entries and list of missing names
   * @param {string[]} names - Array of worker names
   * @returns {{cached: Map<string, import('./types').CacheEntry>, missing: string[]}}
   */
  getMany(names) {
    const cached = new Map()
    const missing = []

    for (const name of names) {
      const entry = this.get(name)
      if (entry) {
        cached.set(name, entry)
      } else {
        missing.push(name)
      }
    }

    return { cached, missing }
  }

  /**
   * Batch set - cache multiple results
   * @param {import('./types').LookupResult[]} results - Array of lookup results
   */
  setMany(results) {
    for (const result of results) {
      this.set(result.name, result)
    }
  }

  /**
   * Invalidate a single cache entry
   * @param {string} name - Worker name to invalidate
   */
  invalidate(name) {
    this.cache.delete(name)
  }

  /**
   * Clear all cache entries
   */
  invalidateAll() {
    this.cache.clear()
  }
}
